/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { CarouselSlider } from '@/components/blocks/Carousel';
import { ProgressIndicator } from '@/components/blocks/ProgressIndicator';
import { RenderContent } from '@/components/blocks/RenderContent';
import { useContentRecommendation } from '@/data/Content/ContentRecommendation';
import { useContentEvents } from '@/data/Content/_ContentEvents';
import { useDeviceType } from '@/data/deviceDetection';
import { ID } from '@/data/types/Basic';
import { CarouselOptions } from '@/data/types/Carousel';
import { WidgetProperties } from '@/data/types/Slot';
import { getCarouselProperties } from '@/utils/getCarouselProperties';
import { renderContent } from '@/utils/renderContent';
import { Theme, useMediaQuery } from '@mui/material';
import React, { FC, useMemo } from 'react';
import { ProcessedContent } from '@/data/types/Marketing';

export const ContentRecommendationSingle: FC<{ content: ProcessedContent }> = ({ content }) => {
	const { onContentClick } = useContentEvents();

	return (
		<RenderContent
			key={`${content.id}${content.contentId}`}
			content={content}
			onClick={onContentClick(content)}
		/>
	);
};

export const ContentRecommendationMultiple: FC<{
	contents: ProcessedContent[];
	properties: WidgetProperties;
}> = ({ contents, properties }) => {
	const { onContentClick } = useContentEvents();

	const { isMobile: smMatch } = useDeviceType();
	const lgMatch = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
	const mdMatch = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

	const visibleSlideLimit = useMemo(
		() => (smMatch ? 2 : mdMatch ? 3 : lgMatch ? 4 : 4),
		[lgMatch, mdMatch, smMatch]
	);

	const defaultProps: CarouselOptions = useMemo(
		() => ({
			naturalSlideWidth: 600,
			naturalSlideHeight: 400,
			visibleSlides: Math.min(contents.length, visibleSlideLimit),
		}),
		[contents.length, visibleSlideLimit]
	);

	const slides = useMemo(
		() => contents.map((c) => renderContent(c, onContentClick(c))).filter(Boolean),
		[contents, onContentClick]
	);

	const carouselProps = useMemo(
		() => getCarouselProperties(properties, defaultProps),
		[defaultProps, properties]
	);

	return (
		<CarouselSlider
			slides={slides}
			carouselProps={carouselProps}
			hideNavButtons={contents.length <= (defaultProps.visibleSlides as number)}
		></CarouselSlider>
	);
};

export const ContentRecommendation: FC<{ id: ID; properties?: WidgetProperties }> = ({
	id: _id,
	properties = {},
}) => {
	const { emsName = '' } = properties;
	const { data, loading, title } = useContentRecommendation(emsName);
	const { onContentClick } = useContentEvents();
	const contents = useMemo(() => data ?? [], [data]);

	return loading ? (
		<ProgressIndicator padding={0} />
	) : (
		// eslint-disable-next-line react/forbid-component-props,react/forbid-elements
		<div className="content-recomnedation-widget" id={_id.toString()}>
			{title?.map((content) => (
				<RenderContent
					key={`${content.id}${content.contentId}`}
					content={content}
					onClick={onContentClick(content)}
				/>
			))}

			{contents.length === 1 ? <ContentRecommendationSingle content={contents[0]} /> : null}
			{contents.length > 1 ? (
				<ContentRecommendationMultiple contents={contents} properties={properties} />
			) : null}
		</div>
	);
};
